/**
 * Global fetch interceptor.
 *
 * The app makes authenticated `fetch` calls from many components using a JWT
 * that silently expires after 24h. When it expires, every protected endpoint
 * returns 401 and the user sees confusing, feature-specific errors (vessel
 * inventory "could not connect to server", serial auto-fill failing, Calor PDF
 * "Unexpected server response (401)"). Rather than handling 401 in each caller,
 * we intercept once here: any 401 from our API clears the session and sends the
 * user to a clean login screen with an explanatory message.
 */

import { handleUnauthorized } from './session';

// Only react to 401s coming from our own backend, not third parties.
const API_HOST = 'app.pvslimited.co.uk';

const getUrl = (input) => {
  try {
    if (typeof input === 'string') return input;
    if (input instanceof Request) return input.url;
    if (input && typeof input.url === 'string') return input.url;
  } catch {
    /* ignore */
  }
  return '';
};

const isOurApi = (url) => {
  if (!url) return false;
  // Absolute URL to our backend, or a same-origin relative /api/... path.
  return url.includes(`${API_HOST}/api`) || url.startsWith('/api');
};

// A 401 on the login endpoint means "wrong credentials", not an expired
// session — never force a redirect for that.
const isLoginRequest = (url) => url.includes('/api/auth/login');

export const installFetchInterceptor = () => {
  if (typeof window === 'undefined' || window.__pvsFetchIntercepted) return;
  const originalFetch = window.fetch.bind(window);

  window.fetch = async (input, init) => {
    const response = await originalFetch(input, init);
    try {
      const url = getUrl(input);
      if (response.status === 401 && isOurApi(url) && !isLoginRequest(url)) {
        handleUnauthorized();
      }
    } catch {
      /* never let interceptor logic break a real request */
    }
    return response;
  };

  window.__pvsFetchIntercepted = true;
};
